import { useState } from "react";
import {
  Button,
  CustomSelect,
  FormFieldSet,
  Modal,
} from "../../../../components";
import { DATE_RANGE_OPTIONS } from "../../../../assets/data/enumData";
import ActivityModal from "./ActivityModal";
import selectOpionGenerator from "../../../../components/Table/TableAdv/tableUtils/selectOpionGenerator";
import { getPaginationRange } from "../../../../components/Table/TableAdv/TableSettings";

const fmt = (n) =>
  n != null
    ? n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "";

const fmtZ = (n) =>
  (n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

function ChargeBackEntryBlock({ entry, isLast }) {
  return (
    <>
      {/* Customer header row */}
      <tr>
        <td
          className="px-2 py-1 text-sm font-semibold text-gray-800"
          colSpan={13}
        >
          {entry?.customer}
        </td>
      </tr>

      {entry?.rows?.map((r, i) => (
        <tr key={i} className="hover:bg-gray-50 transition-colors">
          <td className="px-2 py-1 text-sm text-center text-gray-700"></td>
          <td className="px-2 py-1 text-sm text-center text-gray-700">
            {r?.posted}
          </td>
          <td className="px-2 py-1 text-sm text-center text-gray-700">
            {r?.invoiceNo}
          </td>
          <td className="px-2 py-1 text-sm text-center text-gray-700">
            {r?.purchased}
          </td>
          <td className="px-2 py-1 text-sm text-center text-gray-700">
            {r?.buyNo}
          </td>
          <td className="px-2 py-1 text-sm text-center text-gray-700">
            {r?.invoiceAmount}
          </td>
          <td
            className={`px-2 py-1 text-sm text-center whitespace-nowrap text-gray-700`}
          >
            {r?.amountPaid}
          </td>
          <td className="px-2 py-1 text-sm text-center text-gray-700">
            {r?.paidOn}
          </td>
          <td className="px-2 py-1 text-sm text-center text-gray-700">
            {r?.colNo}
          </td>
          <td className="px-2 py-1 text-sm text-center text-gray-700">
            {r?.giveBack}
          </td>
          <td className="px-2 py-1 text-sm text-center text-gray-700">
            {r?.chargeBack}
          </td>
          <td className="px-2 py-1 text-sm text-center text-gray-700">
            {r?.writeOffer}
          </td>
          <td className="px-2 py-1 text-sm text-center text-gray-700">
            {r?.osDescription}
          </td>
        </tr>
      ))}

      {/* Entry Total */}
      <tr className={!isLast ? "border-b border-gray-400" : ""}>
        <td
          colSpan={4}
          className="px-2 py-1 text-sm font-bold text-right text-gray-700"
        >
          Total
        </td>
        <td className="px-2 py-1 text-sm text-center"></td>
        <td className="px-2 py-1 text-sm text-center"></td>
        <td className="px-2 py-1 text-sm text-center font-semibold text-primary-500 whitespace-nowrap">
          {entry?.total?.amountPaid != null ? entry?.total?.amountPaid : ""}
        </td>
        <td className="px-2 py-1 text-sm text-center"></td>
        <td className="px-2 py-1 text-sm text-center"></td>
        <td className="px-2 py-1 text-sm text-center font-semibold text-primary-500 whitespace-nowrap">
          {entry?.total?.giveBack != null ? entry?.total?.giveBack : ""}
        </td>
        <td className="px-2 py-1 text-sm text-center font-semibold text-primary-500 whitespace-nowrap">
          {entry?.total?.chargeBack}
        </td>
        <td className="px-2 py-1 text-sm text-center"></td>
        <td className="px-2 py-1 text-sm text-center"></td>
      </tr>

      <tr>
        <td colSpan={13} className="h-1"></td>
      </tr>
    </>
  );
}

export const ChargeBackReportTable = ({ rows, columns }) => {
  const [data] = useState(rows);
  const DEFAULT_LIST = [10, 20, 30];
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const totalPages = Math.ceil(data?.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRecords = data?.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (val) => {
    setRowsPerPage(Number(val));
    setCurrentPage(1);
  };

  const toNum = (v) => {
    if (v == null || v === "") return 0;
    const cleaned = String(v)
      .replace(/,/g, "")
      .replace(/[^\d.-]/g, "");
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : 0;
  };
  const grandTotal = currentRecords?.reduce(
    (acc, entry) => ({
      amountPaid: acc?.amountPaid + toNum(entry?.total?.amountPaid),
      giveBack: acc?.giveBack + toNum(entry?.total?.giveBack),
      chargeBack: acc?.chargeBack + toNum(entry?.total?.chargeBack),
    }),
    { amountPaid: 0, giveBack: 0, chargeBack: 0 },
  );
  const pages = getPaginationRange(currentPage, totalPages);

  return (
    <div className="bg-white min-h-full">
      <div className="py-2 px-2 flex items-center gap-2 text-sm flex-wrap">
        <span>
          {!data.length ? startIndex : startIndex + 1}–
          {Math.min(endIndex, data.length)}
        </span>
        <span className="text-nowrap">of {data?.length}</span>

        <Button
          size="sm"
          type="icon"
          variant="text"
          color="black"
          icon="chevron_left"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          data-dropdown-persist="true"
        />

        <div className="flex items-center">
          {pages?.map((page, index) =>
            page === "..." ? (
              <span key={`ellipsis-${index}`} className="px-0! text-gray-400">
                ...
              </span>
            ) : (
              <Button
                key={`page-${page}-${index}`}
                size="sm"
                variant={currentPage === page ? "contained" : "text"}
                color={currentPage === page ? "primary" : "black"}
                title={String(page)}
                className="min-w-[20px] !px-0 !py-0"
                onClick={() => handlePageChange(page)}
                data-dropdown-persist="true"
              />
            ),
          )}
        </div>

        <Button
          size="sm"
          icon="chevron_right"
          type="icon"
          variant="text"
          color="black"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          data-dropdown-persist="true"
        />

        <div className="w-32 lg:w-40">
          <CustomSelect
            size="sm"
            placeholder="Select rows"
            options={selectOpionGenerator(DEFAULT_LIST, rowsPerPage)}
            selected={String(rowsPerPage)}
            onChange={handleRowsPerPageChange}
            data-dropdown-persist="true"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border">
          <thead>
            <tr className="border-t-2 border-b-2 border-gray-900 bg-primary-50">
              {columns?.map((col, i) => (
                <th
                  key={i}
                  className="px-2 py-3 text-xs font-bold whitespace-nowrap text-center"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRecords?.map((entry, i) => (
              <ChargeBackEntryBlock
                key={i}
                entry={entry}
                isLast={i === currentRecords?.length - 1}
              />
            ))}

            <tr className="bg-primary-50 border-t-2 border-b-2 border-primary-300">
              <td
                colSpan={4}
                className="px-2 py-1.5 text-sm font-bold text-right text-gray-800 text-nowrap"
              >
                Grand Total
              </td>
              <td className="px-2 py-1.5 text-sm"></td>
              <td className="px-2 py-1.5 text-sm"></td>
              <td className="px-2 py-1.5 text-sm text-center font-bold text-primary-500 whitespace-nowrap">
                {grandTotal?.amountPaid}
              </td>
              <td className="px-2 py-1.5 text-sm"></td>
              <td className="px-2 py-1.5 text-sm"></td>
              <td className="px-2 py-1.5 text-sm text-center font-bold text-primary-500 whitespace-nowrap">
                {grandTotal?.giveBack}
              </td>
              <td className="px-2 py-1.5 text-sm text-center font-bold text-primary-500 whitespace-nowrap">
                {grandTotal?.chargeBack}
              </td>
              <td className="px-2 py-1.5 text-sm"></td>
              <td className="px-2 py-1.5 text-sm"></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
export const ChargeBackFilterBar = ({
  setActivityOpen,
  activityOpen,
  formData,
  handleChange,
  setChargeBackUpload,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const isCustomRange = formData?.reportPeriod === "custom";

  const handleDateRangeSelect = (val) => {
    handleChange("reportPeriod", val);

    if (val === "custom") {
      handleChange("fromDate", null);
      handleChange("toDate", null);
    } else {
      const range = getDatesByOption(val);
      if (range) {
        handleChange("fromDate", range?.from);
        handleChange("toDate", range?.to);
      }
    }
    setShowDropdown(false);
  };

  return (
    <>
      <div className="bg-white py-3 flex items-end gap-3 flex-wrap">
        {/* Date Range Select */}
        <FormFieldSet
          title="Report Period"
          placeholder="select Date Range"
          type="select"
          options={DATE_RANGE_OPTIONS}
          value={formData?.reportPeriod}
          onChange={handleDateRangeSelect}
          showDropdown={showDropdown}
          // onClick={() => setShowDropdown(true)}
        />

        {/* From - Only for Custom */}
        {isCustomRange && (
          <FormFieldSet
            title="From"
            type="datepicker"
            placeholder="select Date"
            value={formData?.fromDate}
            onChange={(val) => handleChange("fromDate", val)}
            showDropdown={false}
          />
        )}

        {/* To - Only for Custom */}
        {isCustomRange && (
          <FormFieldSet
            title="To"
            type="datepicker"
            placeholder="select Date"
            value={formData?.toDate}
            onChange={(val) => handleChange("toDate", val)}
            showDropdown={false}
            minDate={
              formData?.fromDate ? new Date(formData?.fromDate) : undefined
            }
          />
        )}

        <FormFieldSet
          noTitle
          type="text"
          placeholder="search"
          value={formData?.search}
          onChange={(val) => handleChange("search", val)}
        />

        <div className="flex-1" />

        <Button
          title={"Activity"}
          variant={"outlined"}
          onClick={() => setActivityOpen(true)}
        />
        <Button
          icon={"upload"}
          title={"Upload"}
          onClick={() => setChargeBackUpload(true)}
        />
      </div>

      <Modal
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
        title="Activity"
        width="max-w-7xl"
        titleColor={"primary"}
        zIndex={55}
      >
        <ActivityModal
          isOpen={activityOpen}
          onClose={() => setActivityOpen(false)}
          type={"Chargeback Report"}
        />
      </Modal>
    </>
  );
};
